import {
  Alert,
  Box,
  Button,
  SxProps,
  Typography,
  styled
} from "@outschool/backpack";
import {
  Enrollment as EnrollmentBackendType,
  Section as SectionBackendType
} from "@outschool/gql-backend-generated";
import { ParentUpcomingClassesQueryQuery } from "@outschool/gql-frontend-generated";
import { Trans, useTranslation } from "@outschool/localization";
import {
  joinMembershipPath,
  leaderPath,
  learnerSchedulePath
} from "@outschool/routes";
import SectionCls from "@outschool/section";
import { useSession } from "@outschool/ui-auth";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import { compact } from "lodash";
import React from "react";

import { learnScheduleUrl, transcriptsPath } from "../../../shared/Routes";
import useParentDashbordClassList from "../../hooks/useParentDashbordClassList";
import { useWaitlistSeatsForParentQuery } from "../../queries/WaitlistQuery";
import { TabType, ViewType } from "../../routes/ParentSchedulePage";
import Link from "../Link";
import ClassList from "./ClassList";
import ClassListTabs from "./ClassListTabs";
import NextMeetingsOfParent from "./NextMeetingsOfParent";

export type SectionType =
  // @ts-ignore TS(2339): Property 'activeEnrollments' does not exist on typ... Remove this comment to see the full error message
  ParentUpcomingClassesQueryQuery["currentUser"]["activeEnrollments"][0]["section"];
export type LeaderType = SectionType["leader"];
export type LearnerType =
  // @ts-ignore TS(2339): Property 'activeEnrollments' does not exist on typ... Remove this comment to see the full error message
  ParentUpcomingClassesQueryQuery["currentUser"]["activeEnrollments"][0]["learner"];
export type EnrollmentType = EnrollmentBackendType & {
  section: SectionBackendType;
};
export const ClassImage = styled("img")({
  borderRadius: "8px",
  objectFit: "cover"
});

export const Seperator = () => (
  <Box
    sx={{
      color: "grey.200",
      marginX: 8
    }}
  >
    •
  </Box>
);

export interface ClassDescriptionProps {
  enrollment: EnrollmentType;
  learner: LearnerType;
  section: SectionType;
}

interface ClassTeacherProps {
  leader: LeaderType;
  isLinkComponent: boolean;
}

enum ClassListTab {
  Upcoming = "upcoming",
  Completed = "completed"
}

export const ClassTeacher = ({
  leader,
  isLinkComponent = true
}: ClassTeacherProps) => (
  <Box
    component={isLinkComponent ? Link : "div"}
    target="_blank"
    to={
      isLinkComponent ? leaderPath(leader.uid, leader.leader_link) : undefined
    }
    onClick={(event: React.MouseEvent<HTMLElement>) => {
      // Stop link click from bubbling to link underneath
      event.stopPropagation();
    }}
    aria-label={leader?.name}
    flex
    sx={{
      alignItems: "center",
      gap: 8
    }}
  >
    <UserHeadshotImage user={leader} size={32} />
    <Typography
      variant="body2"
      sx={{
        color: "grey.600",
        width: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {leader.name}
    </Typography>
  </Box>
);

export interface ClassProps {
  enrollment: EnrollmentType;
  sx?: SxProps;
}

export interface ClassListInfoProps {
  isLoading: boolean;
  numClasses: number;
}

const Section = new SectionCls();

interface LoggedInHomeClassesListProps {
  filterByLearnerUid?: string | null;
}

export const LoggedInHomeMyClassesList = ({
  filterByLearnerUid
}: LoggedInHomeClassesListProps) => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\LoggedInHomeMyClassesList"
  );
  const { currentUser } = useSession();
  const isMobile = useIsMobile();
  const { waitlistSeats } = useWaitlistSeatsForParentQuery({
    parentUid: currentUser?.uid
  });

  const hasOpenSeats = React.useMemo(
    () =>
      compact(waitlistSeats).some(
        ws => !!Section.publicallyAvailableSpaces(ws.section)
      ),
    [waitlistSeats]
  );

  const [selectedTab, setSelectedTab] = React.useState<ClassListTab>(
    ClassListTab.Upcoming
  );

  const {
    upcomingEnrollments,
    upcomingEnrollmentsLoading,
    upcomingEnrollmentsError,
    upcomingEnrollmentsTotalCount,
    completedClasses,
    completedClassesLoading,
    completedClassesError,
    completedClassesTotalCount
  } = useParentDashbordClassList({ filterByLearnerUid, limit: 3 });

  const hasMoreUpcomingClasses =
    upcomingEnrollmentsTotalCount > upcomingEnrollments.length;
  const hasMoreCompletedClasses =
    completedClassesTotalCount > completedClasses.length;

  const listLoading = upcomingEnrollmentsLoading || completedClassesLoading;

  const classTabs = [
    {
      label: t("Upcoming classes"),
      value: ClassListTab.Upcoming
    },
    ...(completedClasses.length > 0
      ? [{ label: t("Completed classes"), value: ClassListTab.Completed }]
      : [])
  ];

  const learnerCount = currentUser?.children?.length ?? 0;
  const upcomingEmptyMessage =
    learnerCount === 0
      ? t("Add a learner and start discovering classes")
      : t("Discover classes for every learning need");

  if (isMobile) {
    return (
      <Box sx={{ marginTop: "2.4rem", marginBottom: "3.2rem" }}>
        <Typography
          variant="h4"
          sx={{ textAlign: "center", marginBottom: "1.6rem" }}
        >
          {t("Upcoming classes")}
        </Typography>
        {!listLoading && hasOpenSeats && <AvailableSeatsFromWaitlistBanner />}
        <NextMeetingsOfParent />
        <ClassList
          enrollments={upcomingEnrollments}
          error={upcomingEnrollmentsError}
          loading={upcomingEnrollmentsLoading}
          emptyMessage={upcomingEmptyMessage}
        />
        {hasMoreUpcomingClasses && <ViewMoreButton path={learnScheduleUrl()} />}
        {completedClasses.length > 0 && (
          <>
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                marginTop: "2.4rem",
                marginBottom: "1.6rem"
              }}
            >
              {t("Completed classes")}
            </Typography>
            <ClassList
              enrollments={completedClasses}
              error={completedClassesError}
              loading={completedClassesLoading}
              completed
            />
            {hasMoreCompletedClasses && (
              <ViewMoreButton path={transcriptsPath()} />
            )}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ marginTop: "2.4rem", marginBottom: "4.8rem" }}>
      <ClassListTabs
        tabs={classTabs}
        selectedTab={selectedTab}
        onChange={(value: string) => setSelectedTab(value as ClassListTab)}
        sx={{ marginBottom: "2.4rem" }}
      />
      {!listLoading && hasOpenSeats && <AvailableSeatsFromWaitlistBanner />}
      <NextMeetingsOfParent />
      {selectedTab === ClassListTab.Upcoming && (
        <>
          <ClassList
            enrollments={upcomingEnrollments}
            error={upcomingEnrollmentsError}
            loading={upcomingEnrollmentsLoading}
            emptyMessage={upcomingEmptyMessage}
          />
          {hasMoreUpcomingClasses && (
            <ViewMoreButton path={learnScheduleUrl()} />
          )}
        </>
      )}
      {selectedTab === ClassListTab.Completed && (
        <>
          <ClassList
            enrollments={completedClasses}
            error={completedClassesError}
            loading={completedClassesLoading}
            completed
          />
          {hasMoreCompletedClasses && (
            <ViewMoreButton path={transcriptsPath()} />
          )}
        </>
      )}
    </Box>
  );
};

const AvailableSeatsFromWaitlistBanner = () => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\LoggedInHomeMyClassesList"
  );
  return (
    <Alert
      severity="info"
      title={t("Seats have opened up for your waitlisted classes")}
      sx={{
        marginBottom: "2rem"
      }}
    >
      <Typography>
        <Trans t={t}>
          You can check your waitlisted classes in{" "}
          <Link to={learnerSchedulePath(ViewType.Classes, TabType.Waitlisted)}>
            {t("All Classes")}
          </Link>
          .{" "}
          <Link
            trackingName="AvailableSeatsFromWaitlistBannerToJoinMembership"
            to={joinMembershipPath()}
          >
            Members
          </Link>{" "}
          have 24 hours to enroll before non-members.
        </Trans>
      </Typography>
    </Alert>
  );
};

const ViewMoreButton = ({ path }: { path: string }) => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\LoggedInHomeMyClassesList"
  );
  const navigate = useNavigation();
  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Button
        variant="text"
        textColor="primary"
        sx={{ marginTop: "2rem" }}
        onClick={() => navigate(path)}
      >
        {t("View more")}
      </Button>
    </Box>
  );
};
